import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  TextField,
  TablePagination,
  Button,
  Box,
  InputAdornment,
  Modal,
  CircularProgress,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import axios from "axios";
import { viewImage, viewImageOnNewTab } from "../../../utils/viewPDF";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  textAlign: "center",
};

const EMDTable = ({ vendors, isLoading, getEMD }) => {
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const [status, setStatus] = useState();
  const [reason, setReason] = useState();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleOpen = (s) => {
    console.log(selected);
    setOpen(true);
    setStatus(s);
  };
  const handleClose = () => {
    setOpen(false);
    setStatus("");
    setReason("");
  };

  const groupedData = useMemo(() => {
    const filtered =
      vendors &&
      vendors?.filter(
        (v) =>
          v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          v.emdAmount.includes(searchTerm) ||
          v.utr.toLowerCase().includes(searchTerm.toLowerCase())
      );

    return filtered?.reduce((acc, vendor) => {
      const tenderTitle = vendor.title;
      if (!acc[tenderTitle]) acc[tenderTitle] = [];
      acc[tenderTitle].push(vendor);
      return acc;
    }, {});
  }, [vendors, searchTerm]);

  const handleSelectAllClick = (event, tenderVendors) => {
    const vtIds = tenderVendors.map((v) => v.vtId);

    if (event.target.checked) {
      // Select all: merge existing selected with new ones and remove duplicates
      const newSelected = Array.from(new Set([...selected, ...vtIds]));
      setSelected(newSelected);
    } else {
      // Deselect all: remove only the tender-specific ids
      const filtered = selected.filter((id) => !vtIds.includes(id));
      setSelected(filtered);
    }
  };

  const handleClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((selectedId) => selectedId !== id);
    }
    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const paginatedData = useMemo(() => {
    const entries = Object.entries(groupedData || {});
    const flatData = entries.flatMap(([title, list]) => list);
    const paged = flatData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
    const grouped = {};
    paged.forEach((vendor) => {
      const title = vendor.title;
      if (!grouped[title]) grouped[title] = [];
      grouped[title].push(vendor);
    });
    return grouped;
  }, [groupedData, page, rowsPerPage]);

  const handleVendorTenderUpdate = async () => {
    try {
      const payload = selected.map((id) => ({
        vtId: id,
        status: status,
        username: storedUser.username,
      }));

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/vendor-tender`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setSelected([]);
      setOpen(false);
      getEMD();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Paper
      sx={{ width: "100%", overflow: "hidden", boxShadow: "none !important" }}
    >
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        {storedUser.userRole[0] === "FINANCE_HEAD" && (
          <Box>
            <Button
              className="accept-all"
              onClick={() => handleOpen("EMD_CMD_ACCEPTED")}
            >
              Approve Selected
            </Button>
            <Button
              className="reject-all"
              onClick={() => handleOpen("DISQUALIFIED")}
            >
              Reject Selected
            </Button>
          </Box>
        )}
        <Box
          display="flex"
          justifyContent="end"
          alignItems="center"
          gap={1}
          p={2}
        >
          <TextField
            placeholder="Search"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
              "&:hover": {
                borderRadius: "12px",
                background: "#D8E9F0",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {/* <Button
            startIcon={<FilterListIcon />}
            sx={{
              height: 40,
              border: "1px solid #c7c7c7 !important",
              borderRadius: "12px",
              color: "#000",
              textTransform: "none",
              backgroundColor: "none",
              ":hover": {
                backgroundColor: "none",
              },
            }}
            onClick={() => {
              // Optional: Toggle a filter modal/panel if needed
              console.log("Filter button clicked");
            }}
          >
            Filter
          </Button> */}
        </Box>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selected.length > 0 &&
                    selected.length <
                      Object.values(paginatedData || {}).flat().length
                  }
                  checked={
                    selected.length > 0 &&
                    selected.length ===
                      Object.values(paginatedData || {}).flat().length
                  }
                  onChange={(event) =>
                    handleSelectAllClick(
                      event,
                      Object.values(paginatedData || {}).flat()
                    )
                  }
                />
              </TableCell>
              <TableCell sx={{ width: `${100 / 5}%` }}>Name</TableCell>
              <TableCell sx={{ width: `${100 / 5}%` }}>EMD Amount</TableCell>
              <TableCell sx={{ width: `${100 / 5}%` }}>UTR</TableCell>
              <TableCell sx={{ width: `${100 / 5}%` }}>View</TableCell>
              <TableCell sx={{ width: `${100 / 5}%` }}>
                Mark As Received
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : Object.keys(paginatedData || {}).length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No rows
                </TableCell>
              </TableRow>
            ) : (
              Object.entries(paginatedData).map(([title, vendors]) => (
                <React.Fragment key={title}>
                  <TableRow sx={{ background: "#EAF4FF" }}>
                    <TableCell>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                      ></Typography>
                    </TableCell>
                    <TableCell colSpan={6}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {title}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {vendors.map((row) => {
                    const isItemSelected = isSelected(row.vtId);
                    return (
                      <TableRow
                        key={row.veId}
                        hover
                        role="checkbox"
                        selected={isItemSelected}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            onChange={() => handleClick(row.vtId)}
                          />
                        </TableCell>
                        <TableCell>
                          <strong>{row.name}</strong>
                        </TableCell>
                        <TableCell>{row.emdAmount}</TableCell>
                        <TableCell>{row.utr}</TableCell>
                        <TableCell>
                          {row.document ? (
                            <Button
                              className="btn-contained"
                              sx={{
                                margin: "0 !important",
                                width: "0 !important",
                              }}
                              onClick={() =>
                                viewImageOnNewTab(row.document, storedUser)
                              }
                            >
                              View
                            </Button>
                          ) : (
                            "Not uploaded"
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            className="btn-outlined"
                            sx={{ margin: "0 !important", fontSize: "12px" }}
                            onClick={() => {
                              handleClick(row.vtId);
                              handleOpen("EMD_CMD_ACCEPTED");
                            }}
                          >
                            Mark as Received
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={Object.values(groupedData || {}).flat().length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={10}
        rowsPerPageOptions={[]}
      />

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography
            id="modal-modal-title"
            variant="h5"
            fontWeight={600}
            mb={2}
          >
            Mark EMD as Received?
          </Typography>
          <Typography>
            Please ensure you have checked the details carefully. <br />
            This action is not reversible.
          </Typography>
          <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
            <Button className="btn-outlined btn-reject" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="btn-contained"
              onClick={handleVendorTenderUpdate}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
    </Paper>
  );
};

export default EMDTable;
